import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '../lib/api';
import { useAuth } from './AuthContext';

const AnnouncementContext = createContext(null);

const getStorageKey = (userId) => `crickethub-read-announcements:${userId}`;

export const AnnouncementProvider = ({ children }) => {
  const { user, getAuthHeader, isOrganizer } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [readIds, setReadIds] = useState([]);

  useEffect(() => {
    if (!user?.id) {
      setReadIds([]);
      return;
    }

    try {
      const stored = window.localStorage.getItem(getStorageKey(user.id));
      const parsed = stored ? JSON.parse(stored) : [];
      setReadIds(Array.isArray(parsed) ? parsed : []);
    } catch (error) {
      setReadIds([]);
    }
  }, [user?.id]);

  const fetchAnnouncements = useCallback(async () => {
    if (!user?.id) {
      setAnnouncements([]);
      return;
    }

    setLoading(true);
    try {
      const tournamentsResponse = isOrganizer()
        ? await axios.get(`${API}/tournaments?organizer_only=true`, getAuthHeader())
        : await axios.get(`${API}/tournaments/public`);

      const tournaments = (tournamentsResponse.data || []).slice(0, 6);

      const announcementResponses = await Promise.all(
        tournaments.map((tournament) =>
          axios.get(`${API}/tournaments/${tournament.id}/announcements`, getAuthHeader())
        )
      );

      const merged = announcementResponses
        .flatMap((response, index) =>
          (response.data || []).map((announcement) => ({
            ...announcement,
            tournament_name: tournaments[index]?.name || 'Tournament',
          }))
        )
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 12);

      setAnnouncements(merged);
    } catch (error) {
      setAnnouncements([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id, isOrganizer, getAuthHeader]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  useEffect(() => {
    if (!user?.id) return undefined;

    const interval = window.setInterval(fetchAnnouncements, 30000);
    const handleFocus = () => fetchAnnouncements();

    window.addEventListener('focus', handleFocus);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchAnnouncements, user?.id]);

  const unreadAnnouncements = useMemo(
    () => announcements.filter((announcement) => !readIds.includes(announcement.id)),
    [announcements, readIds]
  );

  const markAsRead = useCallback(
    (announcementId) => {
      if (!user?.id || !announcementId) return;
      setReadIds((current) => {
        if (current.includes(announcementId)) return current;
        const next = [...current, announcementId];
        window.localStorage.setItem(getStorageKey(user.id), JSON.stringify(next));
        return next;
      });
    },
    [user?.id]
  );

  const markAllAsRead = useCallback(() => {
    if (!user?.id) return;
    const next = announcements.map((announcement) => announcement.id);
    setReadIds(next);
    window.localStorage.setItem(getStorageKey(user.id), JSON.stringify(next));
  }, [announcements, user?.id]);

  const value = useMemo(
    () => ({
      announcements,
      unreadAnnouncements,
      unreadCount: unreadAnnouncements.length,
      loading,
      markAsRead,
      markAllAsRead,
      refreshAnnouncements: fetchAnnouncements,
    }),
    [announcements, unreadAnnouncements, loading, markAsRead, markAllAsRead, fetchAnnouncements]
  );

  return <AnnouncementContext.Provider value={value}>{children}</AnnouncementContext.Provider>;
};

export const useAnnouncements = () => {
  const context = useContext(AnnouncementContext);
  if (!context) {
    throw new Error('useAnnouncements must be used within an AnnouncementProvider');
  }
  return context;
};
